import { createContext, useContext, useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import CLUB from '../config/club.js'
import { PbccAdminSidebar } from '../components/PbccLayout.jsx'
import { PBCC_PREMIUM, FOURBALL_ONLY } from '../config/siteMode.js'
import { useAdminZoom } from './adminScale.js'

// Which standard section nav item is active for a given /admin path. Lets the
// shared shell highlight the right sidebar tab without each page telling it.
function sectionFromPath(p) {
  if (p.startsWith('/admin/club-data')) return 'clubdata'
  if (p.startsWith('/admin/demos')) return 'demos'
  if (p.startsWith('/admin/gg-features') || p.startsWith('/admin/gg/formats') ||
      p.startsWith('/admin/gg/multi-format') || p.startsWith('/admin/gg/games')) return 'gfeatures'
  if (p.startsWith('/admin/gg')) return 'gg'
  if (p.startsWith('/admin/pbcc-4ball') || p.startsWith('/admin/t/') ||
      p.startsWith('/admin/qualifying')) return 'tournaments'
  return 'golfops' // /admin, /admin/weather
}

// ── One shared admin shell for the whole premium admin ──────────────────────
// AdminApp wraps its <Routes> in <AdminNavProvider><AdminShell>. Every /admin
// route therefore renders inside the SAME full-height sidebar + edge-to-edge
// #0E281D zoom shell — no per-page shells. Each page publishes its sidebar nav
// (dashboard actions, tournament tabs, or a standalone page's own items) via
// useSetAdminNav(); the shell renders whatever the current page registered.
// Modals (portaled) and AIAssistant (portaled) escape the zoom on their own.

const AdminNavCtx = createContext(null)

export function AdminNavProvider({ children }) {
  const [nav, setNav] = useState(null)
  return <AdminNavCtx.Provider value={{ nav, setNav }}>{children}</AdminNavCtx.Provider>
}

// Pages call this to publish their sidebar nav. `deps` controls when it updates
// (e.g. the active tab). Cleared on unmount so the next page starts fresh.
export function useSetAdminNav(navObj, deps) {
  const ctx = useContext(AdminNavCtx)
  const setNav = ctx?.setNav
  useEffect(() => {
    if (!setNav) return
    setNav(navObj)
    return () => setNav(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)
}

export function AdminShell({ children, nav: navProp }) {
  const zoomRef = useAdminZoom()
  const ctx = useContext(AdminNavCtx)
  const { pathname } = useLocation()
  // Standalone pages outside the /admin router (e.g. TV Control) can pass `nav`
  // directly; pages inside the router publish it via context (useSetAdminNav).
  const nav = navProp !== undefined ? navProp : ctx?.nav
  // ONE shell for the whole premium admin — the same full-height sidebar + zoom
  // frame on EVERY route, so scaling + chrome are identical site-wide (this is
  // what the 4-Ball site does). Non-premium (club/live) is never wrapped.
  if (!PBCC_PREMIUM) return children
  // Big site: pages that don't publish their own nav (Golf Ops, Handicap, Demos,
  // Pro Shop, Tournaments) all share the standard SECTION nav, highlighted by
  // route. 4-Ball — and any page that publishes a nav (tournament tabs) — render
  // the published items instead.
  const showSectionNav = !FOURBALL_ONLY && !nav
  return (
    <div ref={zoomRef} className="pbcc-admin-shell pbcc-admin-frame flex" style={{ background: '#0E281D' }}>
      {showSectionNav ? (
        <PbccAdminSidebar active={sectionFromPath(pathname)} />
      ) : (
        <aside className="pbcc-sidebar pbcc-sidebar-4ball flex flex-col">
          <div className="px-6 pt-5 pb-3 shrink-0">
            <img src={CLUB.logoLight} alt={CLUB.name} style={{ width: '100%', height: 'auto', objectFit: 'contain', display: 'block' }} />
          </div>
          {nav?.back ? (
            <Link
              to={nav.back.to}
              className="mx-4 mb-2 inline-flex items-center gap-1 text-sm font-bold uppercase tracking-widest transition"
              style={{ color: '#C2A24E' }}>
              {nav.back.label || '← Back'}
            </Link>
          ) : null}
          <nav className="flex-1 pt-0 pb-4 px-2 overflow-y-auto min-h-0">
            {(nav?.items || []).map((it) => (
              it.to ? (
                <Link key={it.key} to={it.to} className={`pbcc-sidebar-link ${it.active ? 'pbcc-sidebar-link-active' : ''}`}>
                  {it.label}
                </Link>
              ) : (
                <button key={it.key} type="button" onClick={it.onClick} className={`pbcc-sidebar-link ${it.active ? 'pbcc-sidebar-link-active' : ''}`}>
                  {it.label}
                </button>
              )
            ))}
          </nav>
          {nav?.status ? <div className="pbcc-status-card m-4">{nav.status}</div> : null}
        </aside>
      )}
      <div className="flex-1 flex flex-col min-w-0">{children}</div>
    </div>
  )
}
